import numeral from 'numeral';

import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import Iconmoon from '@deprecated-ui/common/Iconmoon';
import color from '@deprecated-ui/_style/color';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { earningsEffects, earningsSelectors } from 'features/earnings/slice';
import DATA_CY from 'constants/data-cy';
import { useState } from 'react';
import EarningsPagination from '../Pagination';
import { Th, TableEarnings, ModalIconWrapper } from '../../styled';
import ModalEarning from './ModalEarning';

const EARNINGS_TYPE = {
  CONSENSUS: {
    ACTUAL: 'consensusActual',
    ESTIMATE: 'consensusEstimate',
    SURPRISE: 'consensusSurprise',
  },
  PREVIOUS_YEAR: {
    ACTUAL: 'previousYearActual',
    PREVIOUS: 'previousYearPrevious',
    CHANGE: 'previousYearChange',
  },
};

const customRenderNumber = ({ text, isNeedBackgroundColor, name }) => {
  if (name === EARNINGS_TYPE.PREVIOUS_YEAR.CHANGE && !parseFloat(text)) {
    return '-';
  }

  if (text === null || text === '-') {
    return '-';
  }

  return isNeedBackgroundColor
    ? {
        children: `${numeral(text).format('0.00')}%`,
        props: {
          style: {
            background:
              text > 0
                ? 'rgba(144, 232, 151, 0.5)'
                : 'rgba(255, 152, 142, 0.5)',
          },
        },
      }
    : numeral(text).format('0.00');
};

const tableDataGenerator = (companyList: Array<any>) => {
  if (companyList) {
    return companyList.map((item) => ({
      symbol: item.company_name,
      ...item.analyst,
    }));
  }

  return [];
};

const EarningsTable = ({ screenWidth }: { screenWidth: number }) => {
  const dispatch = useDispatch();
  const [rowSelected, setRowSelected] = useState(null);

  const {
    earnings: { data: rawData, isLoading },
  } = useSelector(earningsSelectors);

  const { company, page, totalrows, keyword, year, quarter, filter } = rawData;

  const data = tableDataGenerator(company);

  const columnHeaderClickHandler = (pagination, filters, sorter) => {
    if (!sorter || !sorter.column) {
      return;
    }

    const {
      order,
      column: { dataSortCol },
    } = sorter;

    const sortMode =
      // eslint-disable-next-line no-nested-ternary
      order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : '';

    dispatch(
      earningsEffects.getEarnings({
        companySymbol: keyword || undefined,
        order: sortMode,
        sortcol: dataSortCol,
        page: '1',
        year,
        quarter,
        filter,
      }),
    );
  };

  const columnSymbolRender = (text: string) => ({
    children: (
      <Flex justify="space-between" align="center">
        <Link href={`/symbol/${text}`} passHref>
          <Text
            as="a"
            href={`/symbol/${text}`}
            color={color.primary}
            data-cy={`${DATA_CY.EARNINGS.TABLE_ROW_SYMBOL}-${text}`}
          >
            {`${text}`}
          </Text>
        </Link>
        {screenWidth <= 720 ? (
          <ModalIconWrapper>
            <Iconmoon
              size="14px"
              className="icon-toolbar_next"
              onClick={() => setRowSelected(text)}
            />
          </ModalIconWrapper>
        ) : null}
      </Flex>
    ),
  });

  const consensusChildren = () => {
    const children = [
      {
        title: <Th>Actual</Th>,
        dataIndex: ['consensus', 'actual'],
        sorter: true,
        dataSortCol: 2,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: false,
            name: EARNINGS_TYPE.CONSENSUS.ACTUAL,
          }),
      },
      {
        title: <Th>Estimate</Th>,
        dataIndex: ['consensus', 'estimate'],
        sorter: true,
        dataSortCol: 3,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: false,
            name: EARNINGS_TYPE.CONSENSUS.ESTIMATE,
          }),
      },
      {
        title: <Th>Surprise</Th>,
        dataIndex: ['consensus', 'surprise'],
        sorter: true,
        dataSortCol: 4,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: true,
            name: EARNINGS_TYPE.CONSENSUS.SURPRISE,
          }),
      },
    ];

    if (screenWidth <= 720) {
      return children.splice(2, 1);
    }

    return children;
  };

  const prevYearChildren = () => {
    const children = [
      {
        title: <Th>Actual</Th>,
        dataIndex: ['prevyear', 'actual'],
        sorter: true,
        dataSortCol: 2,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: false,
            name: EARNINGS_TYPE.PREVIOUS_YEAR.ACTUAL,
          }),
      },
      {
        title: <Th>Previous</Th>,
        dataIndex: ['prevyear', 'previous'],
        sorter: true,
        dataSortCol: 5,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: false,
            name: EARNINGS_TYPE.PREVIOUS_YEAR.PREVIOUS,
          }),
      },
      {
        title: <Th>Change</Th>,
        dataIndex: ['prevyear', 'change'],
        sorter: true,
        dataSortCol: 6,
        render: (text) =>
          customRenderNumber({
            text,
            isNeedBackgroundColor: true,
            name: EARNINGS_TYPE.PREVIOUS_YEAR.CHANGE,
          }),
      },
    ];

    if (screenWidth <= 720) {
      return children.splice(2, 1);
    }
    return children;
  };

  const columns = [
    {
      title: <Th />,
      children: [
        {
          title: <Th>Symbol</Th>,
          dataIndex: 'symbol',
          render: columnSymbolRender,
          dataSortCol: 1,
          sorter: (a, b) => a.symbol.localeCompare(b.symbol),
          className: 'symbol',
        },
      ],
    },
    {
      title: <Th>Consensus</Th>,
      children: consensusChildren(),
    },
    {
      title: <Th>Previous Year</Th>,
      children: prevYearChildren(),
    },
  ];

  return (
    <>
      <TableEarnings
        dataSource={data}
        columns={columns}
        pagination={false}
        onChange={columnHeaderClickHandler}
        bordered
        loading={isLoading}
        sortDirections={['ascend', 'descend', 'ascend']}
      />
      <EarningsPagination currentPage={page} totalRow={totalrows} />
      {rowSelected ? (
        <ModalEarning
          visible={!!rowSelected}
          onCancel={() => setRowSelected(null)}
          EARNINGS_TYPE={EARNINGS_TYPE}
          customRenderNumber={customRenderNumber}
          earning={data.find(({ symbol }) => symbol === rowSelected)}
        />
      ) : null}
    </>
  );
};

export default EarningsTable;
